// src/pages/Employees/EmployeeDetail.jsx — Light Theme
// ─────────────────────────────────────────────────────────────────────────────
// Full employee profile page.
// Route: /employees/:id
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useCallback, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Tag, Progress, Tooltip, Tabs, Popconfirm,
  notification, Avatar, Divider, Skeleton,
  Form, Input, Select, DatePicker, Button,
} from "antd";
import {
  ArrowLeftOutlined, UserOutlined, PhoneOutlined,
  MailOutlined, EnvironmentOutlined, TeamOutlined,
  CalendarOutlined, CheckCircleOutlined, ClockCircleOutlined,
  WarningOutlined, DeleteOutlined, EditOutlined,
  TrophyOutlined, FireOutlined, RiseOutlined,
  ExclamationCircleOutlined, IdcardOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import employeeService from "../../services/employeeService";
import StatusBadge from "../../components/StatusBadge";
import LogCallButton from "../../components/CallLog/LogCallButton";
import "./EmployeeDetail.css";
import { useScheduleForCohort, useDay10Questions } from "../../context/MasterDataContext";

// ── Local helpers (replace store functions) ───────────────────────────────────
function daysSinceJoining(doj) {
  if (!doj) return 0;
  return dayjs().diff(dayjs(doj), "day");
}
function _getCall(emp, callKey) {
  const calls = emp?.calls || [];
  return Array.isArray(calls)
    ? calls.find((c) => c.callKey === callKey)
    : calls?.[callKey];
}
function getCallStatus(emp, callKey) {
  const call = _getCall(emp, callKey);
  return call?.status || "Upcoming";
}
function getCallSummary(emp, schedule) {
  const summary = {};
  (schedule || []).forEach(({ key }) => { summary[key] = getCallStatus(emp, key); });
  return summary;
}
function getJourneyProgress(emp, schedule) {
  const calls = emp?.calls || [];
  const arr   = Array.isArray(calls) ? calls : Object.values(calls);
  const done  = arr.filter((c) => c.status === "Completed").length;
  const total = (schedule || []).length || 1;
  return Math.round((done / total) * 100);
}
function getNextPendingCall(emp, schedule) {
  return (schedule || []).find(({ key }) => {
    const st = getCallStatus(emp, key);
    return st === "Pending" || st === "Upcoming";
  }) || null;
}

const C = {
  blue:"#1E6FBF", orange:"#E8871A", navy:"#1B2D6B",
  green:"#16a34a", red:"#dc2626", amber:"#b45309",
  text1:"#1a2d4a", text2:"#4a6580", text3:"#7a96b0",
  border:"#dce6f0", surf:"#f5f8fc",
};

// ── helpers ────────────────────────────────────────────────────────────────
function avatarInitials(name = "") {
  return name.split(" ").slice(0, 2).map((w) => w[0]?.toUpperCase()).join("") || "?";
}
function avatarColor(name = "") {
  const colors = ["#1E6FBF","#1B2D6B","#16a34a","#b45309","#7c3aed","#0891b2","#be185d"];
  let h = 0;
  for (const c of name) h = (h * 31 + c.charCodeAt(0)) % colors.length;
  return colors[h];
}

const STATUS_ICON = {
  Completed: <CheckCircleOutlined  style={{ color: C.green }} />,
  Pending:   <ClockCircleOutlined  style={{ color: C.amber }} />,
  Missed:    <WarningOutlined      style={{ color: C.red   }} />,
  Upcoming:  <RiseOutlined         style={{ color: C.blue  }} />,
};

const DAY10_ANSWER_COLOR = { Yes:"#16a34a", No:"#dc2626", NA:"#7a96b0", "":"#b0c4d8" };

// ─────────────────────────────────────────────────────────────────────────────
export default function EmployeeDetail() {
  const { id }          = useParams();
  const navigate        = useNavigate();
  const [searchParams]  = useSearchParams();
  const initialTab      = searchParams.get("tab") || "calls";
  const [emp,         setEmp]        = useState(null);
  const [loading,     setLoading]    = useState(true);
  const [deleting,    setDeleting]   = useState(false);
  const [callHistory, setCallHistory]= useState([]);   // all history rows for this employee

  // ── Per-cohort schedule (live from MasterData context) ────────
  // Note: day10Questions and sub-component schedules are loaded inside their own components
  const schedule      = useScheduleForCohort(emp?.cohortName);

  const loadEmployee = useCallback(async () => {
    setLoading(true);
    try {
      const [empRes, histRes] = await Promise.all([
        employeeService.getOne(id),
        employeeService.getCallHistory(id),
      ]);
      setEmp(empRes.data);
      // Sort history newest-first within each call key
      setCallHistory(Array.isArray(histRes.data) ? histRes.data : []);
    } catch (err) {
      notification.error({ message: "Failed to load employee", description: err.message });
      navigate("/employees");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { loadEmployee(); }, [loadEmployee]);

  const refresh = useCallback(() => loadEmployee(), [loadEmployee]);

  if (loading) {
    return (
      <div style={{ padding: 24 }}>
        <Skeleton active avatar paragraph={{ rows: 4 }} />
        <Skeleton active paragraph={{ rows: 6 }} style={{ marginTop: 24 }} />
      </div>
    );
  }

  if (!emp) {
    return (
      <div className="ed-not-found">
        <ExclamationCircleOutlined style={{ fontSize: 48, color: C.text3 }} />
        <h3>Employee not found</h3>
        <button className="ed-back-btn" onClick={() => navigate("/employees")}>
          ← Back to Employees
        </button>
      </div>
    );
  }

  // ── derived ──────────────────────────────────────────────────────────────
  const days       = daysSinceJoining(emp.dateOfJoining);
  const progress   = getJourneyProgress(emp, schedule);
  const summary    = getCallSummary(emp, schedule);
  const nextCall   = getNextPendingCall(emp, schedule);
  const completed  = schedule.filter(({ key }) => summary[key] === "Completed").length;
  const missed     = schedule.filter(({ key }) => summary[key] === "Missed").length;
  const pending    = schedule.filter(({ key }) => summary[key] === "Pending").length;

  const journeyPct = Math.min(100, Math.round((days / 180) * 100));
  const isComplete = progress === 100;
  const isAtRisk   = missed > 0;

  async function handleDelete() {
    setDeleting(true);
    try {
      await employeeService.remove(emp.id);
      notification.success({ message: "Employee removed", placement: "topRight" });
      navigate("/employees");
    } catch (err) {
      notification.error({ message: err.message || "Failed to remove employee" });
      setDeleting(false);
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <div className="ed-root">

      {/* ── Top nav bar ── */}
      <div className="ed-topbar">
        <button className="ed-back-btn-sm" onClick={() => navigate("/employees")}>
          <ArrowLeftOutlined /> Back to Employees
        </button>
        <div style={{ display:"flex", gap:10 }}>
          {isAtRisk && (
            <div className="ed-risk-pill">
              <WarningOutlined /> At Risk · {missed} missed call{missed > 1 ? "s" : ""}
            </div>
          )}
          <Popconfirm
            title="Remove this employee?"
            description="This will permanently delete all call records."
            onConfirm={handleDelete}
            okText="Delete" cancelText="Cancel"
            okButtonProps={{ danger:true }}
          >
            <button className="ed-danger-btn" disabled={deleting}>
              <DeleteOutlined /> {deleting ? "Removing…" : "Remove"}
            </button>
          </Popconfirm>
        </div>
      </div>

      {/* ── Profile hero card ── */}
      <div className="ed-hero">
        <div className="ed-hero-left">
          <Avatar
            size={80}
            style={{ background: avatarColor(emp.empName), fontSize:28, fontWeight:800, flexShrink:0 }}
          >
            {avatarInitials(emp.empName)}
          </Avatar>

          <div className="ed-hero-info">
            <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
              <h2 className="ed-hero-name">{emp.empName}</h2>
              {isComplete && (
                <span className="ed-complete-badge">
                  <TrophyOutlined /> Journey Complete
                </span>
              )}
              {isAtRisk && !isComplete && (
                <span className="ed-risk-badge">
                  <FireOutlined /> At Risk
                </span>
              )}
            </div>
            <div className="ed-hero-meta">
              <span><IdcardOutlined /> {emp.empCode}</span>
              <span className="ed-dot">·</span>
              <span>{emp.designation}</span>
              <span className="ed-dot">·</span>
              <Tag style={{ borderRadius:6, margin:0, fontSize:11, fontWeight:600, background:"#eaf1f8", border:"1px solid #c5d6e8", color:C.text2 }}>
                {emp.division}
              </Tag>
              <span className="ed-dot">·</span>
              <span><EnvironmentOutlined /> {emp.hq}</span>
            </div>
            <div className="ed-hero-meta" style={{ marginTop:6 }}>
              {emp.mobile && (
                <span><PhoneOutlined style={{ marginRight:4 }} />{emp.mobile}</span>
              )}
              {emp.empEmail && (
                <>
                  <span className="ed-dot">·</span>
                  <span><MailOutlined style={{ marginRight:4 }} />{emp.empEmail}</span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Journey ring */}
        <div className="ed-hero-ring">
          <Progress
            type="circle"
            percent={progress}
            size={100}
            strokeColor={isComplete ? C.green : isAtRisk ? C.red : C.blue}
            trailColor="#eaf1f8"
            strokeWidth={8}
            format={(p) => (
              <div style={{ textAlign:"center" }}>
                <div style={{ fontSize:22, fontWeight:800, color:C.text1 }}>{p}%</div>
                <div style={{ fontSize:10, color:C.text3, fontWeight:600 }}>Journey</div>
              </div>
            )}
          />
          <div className="ed-ring-label">{completed}/{schedule.length} calls done</div>
        </div>
      </div>

      {/* ── Stats strip ── */}
      <div className="ed-stats-strip">
        {[
          { label:"Day",       value: days > 180 ? "190+" : `Day ${days}`, color:C.blue  },
          { label:"DOJ",       value: dayjs(emp.dateOfJoining).format("DD MMM YYYY"), color:C.text2 },
          { label:"Completed", value: completed, color:C.green  },
          { label:"Pending",   value: pending,   color:C.amber  },
          { label:"Missed",    value: missed,    color:C.red    },
          { label:"Progress",  value: `${journeyPct}%`, color:C.blue },
        ].map(({ label, value, color }) => (
          <div key={label} className="ed-stat-chip">
            <div className="ed-stat-val" style={{ color }}>{value}</div>
            <div className="ed-stat-lbl">{label}</div>
          </div>
        ))}
      </div>

      {/* ── Journey progress bar ── */}
      <div className="ed-journey-bar-wrap">
        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
          <span style={{ fontSize:12, fontWeight:700, color:C.text2 }}>180-Day Journey</span>
          <span style={{ fontSize:11, color:C.text3 }}>Day {Math.min(days,180)} of 180</span>
        </div>
        <Progress
          percent={journeyPct}
          strokeColor={{ "0%": C.blue, "100%": C.navy }}
          trailColor="#eaf1f8"
          strokeWidth={10}
          showInfo={false}
          style={{ borderRadius:6 }}
        />
        {/* milestone ticks */}
        <div className="ed-milestones">
          {schedule.map(({ key, day, label }) => {
            const pct = Math.round((day / 190) * 100);
            const st  = summary[key];
            return (
              <Tooltip key={key} title={`${label} — ${st}`}>
                <div
                  className="ed-milestone-tick"
                  style={{ left:`${pct}%`,
                    background: st === "Completed" ? C.green
                              : st === "Missed"    ? C.red
                              : st === "Pending"   ? C.amber
                              : "#c5d6e8",
                  }}
                />
              </Tooltip>
            );
          })}
        </div>
      </div>

      {/* ── Next pending call banner ── */}
      {nextCall && (
        <div className="ed-next-call-banner">
          <div style={{ display:"flex", alignItems:"center", gap:12 }}>
            <ClockCircleOutlined style={{ fontSize:20, color:C.blue }} />
            <div>
              <div style={{ fontSize:13, fontWeight:700, color:C.text1 }}>
                Next: {nextCall.label} Call
              </div>
              <div style={{ fontSize:11, color:C.text3 }}>
                Due {dayjs(emp.dateOfJoining).add(nextCall.day,"day").format("DD MMM YYYY")}
                {" — "}
                Day {nextCall.day} of the journey
              </div>
            </div>
          </div>
          <LogCallButton
            employee={emp}
            callKey={nextCall.key}
            onSaved={refresh}
            size="middle"
            style={{ minWidth:140 }}
            label={`Log ${nextCall.label} Call`}
          />
        </div>
      )}

      {/* ── Tabs ── */}
      <Tabs
        defaultActiveKey={initialTab}
        className="ed-tabs"
        items={[
          { key:"calls",   label:"Call History",   children: <CallHistoryTab  emp={emp} summary={summary} refresh={refresh} callHistory={callHistory} /> },
          { key:"profile", label:"Profile",         children: <ProfileTab      emp={emp} refresh={refresh} /> },
          { key:"manager", label:"Manager Follow-up",children: <ManagerTab     emp={emp} refresh={refresh} /> },
          // Show Q&A tab for the stage that has hasQuestions=true in this employee's schedule
          ...( () => {
            const qaStage = schedule.find(s => s.hasQuestions);
            if (!qaStage) return [];
            const tabLabel = `${qaStage.label} Responses`;
            return [{ key: qaStage.key, label: tabLabel,
              children: <Day10Tab emp={emp} callKey={qaStage.key} /> }];
          })(),
        ]}
      />
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TAB: Call History
// ══════════════════════════════════════════════════════════════════════════════
// ── HistoryEntry — one saved log entry from call_history table ──
function HistoryEntry({ entry, callKey, isLatest }) {
  const day10Questions = useDay10Questions();
  const STATUS_COLOR = {
    Completed:      { color:"#16a34a", bg:"#f0fdf4", border:"#86efac" },
    "Not Reachable":{ color:"#b45309", bg:"#fffbeb", border:"#fde68a" },
    "Call Later":   { color:"#1E6FBF", bg:"#eff6ff", border:"#bfdbfe" },
    "No Response":  { color:"#dc2626", bg:"#fef2f2", border:"#fca5a5" },
  };
  const cfg = STATUS_COLOR[entry.status] || { color:"#7a96b0", bg:"#f5f8fc", border:"#dce6f0" };
  // snapshot holds the q1..q12 answers for day10
  const snap = entry.snapshot || {};

  return (
    <div style={{
      border:`1px solid ${isLatest ? cfg.border : "#dce6f0"}`,
      borderRadius:10,
      background: isLatest ? cfg.bg : "#fafcff",
      overflow:"hidden",
    }}>
      {/* Entry header */}
      <div style={{
        display:"flex", alignItems:"center", justifyContent:"space-between",
        padding:"10px 14px",
        background: isLatest ? cfg.bg : "#f5f8fc",
        borderBottom:`1px solid ${isLatest ? cfg.border : "#dce6f0"}`,
      }}>
        <div style={{display:"flex",alignItems:"center",gap:10}}>
          {isLatest && (
            <span style={{
              fontSize:10,fontWeight:700,background:cfg.color,color:"#fff",
              padding:"1px 7px",borderRadius:20,letterSpacing:"0.4px",
            }}>LATEST</span>
          )}
          <Tag style={{borderRadius:20,fontWeight:700,fontSize:11,
            color:cfg.color,background:"#fff",border:`1px solid ${cfg.border}`,margin:0}}>
            {entry.status}
          </Tag>
          {entry.callDate && (
            <span style={{fontSize:12,color:"#4a6580",fontWeight:500}}>
              Called on {dayjs(entry.callDate).format("DD MMM YYYY")}
            </span>
          )}
        </div>
        <span style={{fontSize:11,color:"#7a96b0"}}>
          Saved {entry.loggedAt ? dayjs(entry.loggedAt).format("DD MMM YYYY HH:mm") : "—"}
        </span>
      </div>

      {/* Entry body */}
      <div style={{padding:"10px 14px"}}>
        {entry.remarks && (
          <div style={{
            background:"#f5f8fc",borderRadius:8,padding:"8px 12px",
            fontSize:12,color:"#4a6580",marginBottom:10,
            borderLeft:"3px solid #1E6FBF",
          }}>
            <div style={{fontSize:10,fontWeight:700,color:"#7a96b0",
              textTransform:"uppercase",letterSpacing:"0.6px",marginBottom:3}}>Remarks</div>
            {entry.remarks}
          </div>
        )}

        {/* Q&A Responses — shown for any call that has q1..q12 answers (day10 for Pratham, day30 for Campus) */}
        {day10Questions.length > 0 && snap.q1 != null && (
          <div>
            {/* Summary chips */}
            <div style={{display:"flex",gap:6,marginBottom:8,flexWrap:"wrap",alignItems:"center"}}>
              <span style={{fontSize:10,fontWeight:700,color:"#7a96b0",
                textTransform:"uppercase",letterSpacing:"0.6px"}}>Responses:</span>
              {["Yes","No","NA"].map(v => {
                const count = day10Questions.filter(q => snap[q.key] === v).length;
                return count > 0 ? (
                  <span key={v} style={{
                    padding:"1px 8px",borderRadius:20,fontSize:11,fontWeight:700,
                    color:DAY10_ANSWER_COLOR[v],
                    background:v==="Yes"?"#f0fdf4":v==="No"?"#fef2f2":"#f5f8fc",
                    border:`1px solid ${v==="Yes"?"#86efac":v==="No"?"#fca5a5":"#dce6f0"}`,
                  }}>{v}: {count}</span>
                ) : null;
              })}
              <span style={{fontSize:11,color:"#7a96b0"}}>
                {day10Questions.filter(q=>snap[q.key]).length}/{day10Questions.length} answered
              </span>
            </div>
            {/* Question rows */}
            <div style={{display:"flex",flexDirection:"column",gap:3}}>
              {day10Questions.map((q,i) => {
                const ans = snap[q.key] || "—";
                const col = DAY10_ANSWER_COLOR[ans] || "#7a96b0";
                return (
                  <div key={q.key} style={{
                    display:"flex",alignItems:"flex-start",gap:8,
                    padding:"5px 8px",borderRadius:7,
                    background:ans==="Yes"?"#f0fdf4":ans==="No"?"#fef2f2":ans==="NA"?"#f5f8fc":"#fff",
                    border:`1px solid ${ans==="Yes"?"#dcfce7":ans==="No"?"#fecaca":ans==="NA"?"#dce6f0":"#eaf1f8"}`,
                  }}>
                    <span style={{
                      width:16,height:16,borderRadius:"50%",flexShrink:0,
                      background:"#eaf1f8",color:"#7a96b0",fontSize:9,fontWeight:700,
                      display:"flex",alignItems:"center",justifyContent:"center",marginTop:1,
                    }}>{i+1}</span>
                    <span style={{fontSize:12,color:"#1a2d4a",flex:1,lineHeight:1.4}}>{q.label}</span>
                    <span style={{
                      fontSize:11,fontWeight:700,color:col,flexShrink:0,
                      padding:"1px 7px",borderRadius:20,
                      background:ans==="Yes"?"#dcfce7":ans==="No"?"#fecaca":ans==="NA"?"#dce6f0":"#eaf1f8",
                    }}>{ans}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Other call fields from snapshot */}
        {callKey !== "day10" && (snap.outcome || snap.nextAction) && (
          <div style={{display:"flex",flexWrap:"wrap",gap:8}}>
            {snap.outcome && (
              <div>
                <span style={{fontSize:10,fontWeight:700,color:"#7a96b0",
                  textTransform:"uppercase",letterSpacing:"0.6px",marginRight:6}}>Outcome</span>
                <Tag style={{borderRadius:20}}>{snap.outcome}</Tag>
              </div>
            )}
            {snap.nextAction && (
              <div style={{background:"#eff6ff",borderRadius:8,padding:"5px 10px",fontSize:12,color:"#1E6FBF"}}>
                <RiseOutlined style={{marginRight:5}}/>{snap.nextAction}
              </div>
            )}
          </div>
        )}

        {!entry.remarks && callKey!=="day10" && !snap.outcome && !snap.nextAction && (
          <span style={{fontSize:12,color:"#7a96b0"}}>No additional details recorded.</span>
        )}
      </div>
    </div>
  );
}

function CallHistoryTab({ emp, summary, refresh, callHistory = [] }) {
  const schedule       = useScheduleForCohort(emp?.cohortName);
  const [expanded, setExpanded] = React.useState({});

  function getCallData(callKey) {
    const calls = emp?.calls || [];
    return Array.isArray(calls)
      ? (calls.find((c) => c.callKey === callKey) || {})
      : (calls?.[callKey] || {});
  }

  function toggleExpand(key) {
    setExpanded(prev => ({ ...prev, [key]: !prev[key] }));
  }

  const STATUS_DOT = {
    Completed:   { color: C.green,  bg: "#f0fdf4", border: "#86efac",  icon: "✅" },
    Pending:     { color: C.amber,  bg: "#fffbeb", border: "#fde68a",  icon: "⏳" },
    "No Response":{ color: C.red,   bg: "#fef2f2", border: "#fca5a5",  icon: "🚫" },
    Upcoming:    { color: C.text3,  bg: "#f5f8fc", border: "#dce6f0",  icon: "🔵" },
    "Not Reachable":{ color:C.amber,bg:"#fffbeb",  border:"#fde68a",   icon: "📵" },
    "Call Later":{ color: C.blue,   bg: "#eff6ff", border: "#bfdbfe",  icon: "📅" },
  };

  return (
    <div className="ed-tab-content">
      {/* ── Timeline ── */}
      <div style={{ position:"relative", paddingLeft:40 }}>

        {/* Vertical line */}
        <div style={{
          position:"absolute", left:15, top:8, bottom:8,
          width:2, background:"linear-gradient(180deg,#dce6f0 0%,#eaf1f8 100%)",
          borderRadius:2,
        }} />

        {schedule.map(({ key, label, day }, idx) => {
          const st       = summary[key];
          const callData = getCallData(key);
          const dueDate  = dayjs(emp.dateOfJoining).add(day, "day");
          const isComp   = st === "Completed";
          const isLogged = !!callData.callDate;
          const cfg      = STATUS_DOT[st] || STATUS_DOT.Upcoming;
          const isOpen   = expanded[key] ?? isComp;  // auto-expand completed

          return (
            <div key={key} style={{ position:"relative", marginBottom: idx < schedule.length-1 ? 0 : 0 }}>

              {/* ── Timeline dot ── */}
              <div style={{
                position:"absolute", left:-34, top:16,
                width:20, height:20, borderRadius:"50%",
                background: isComp ? C.green : cfg.bg,
                border: `2px solid ${cfg.border}`,
                display:"flex", alignItems:"center", justifyContent:"center",
                fontSize:10, zIndex:1,
                boxShadow: isComp ? `0 0 0 4px #dcfce7` : undefined,
              }}>
                {isComp ? "✓" : <div style={{width:8,height:8,borderRadius:"50%",background:cfg.color}} />}
              </div>

              {/* ── Card ── */}
              <div style={{
                background: "#fff",
                border: `1px solid ${isComp ? "#86efac" : "#dce6f0"}`,
                borderLeft: `3px solid ${cfg.color}`,
                borderRadius:12, marginBottom:10,
                overflow:"hidden",
                boxShadow: isComp ? "0 2px 8px rgba(22,163,74,0.08)" : "0 1px 3px rgba(30,111,191,0.05)",
              }}>

                {/* Card header — always visible */}
                <div
                  style={{
                    display:"flex", alignItems:"center", justifyContent:"space-between",
                    padding:"12px 16px", cursor: isLogged ? "pointer" : "default",
                    background: isComp ? "#f0fdf4" : "#fafcff",
                  }}
                  onClick={() => isLogged && toggleExpand(key)}
                >
                  <div style={{display:"flex",alignItems:"center",gap:10}}>
                    <span style={{fontSize:18}}>{cfg.icon}</span>
                    <div>
                      <div style={{fontWeight:700, fontSize:14, color:C.text1}}>{label}</div>
                      <div style={{fontSize:11, color:C.text3, marginTop:1}}>
                        Due {dueDate.format("DD MMM YYYY")}
                        {callData.callDate && (
                          <span style={{marginLeft:8, color:C.green, fontWeight:600}}>
                            · Called {dayjs(callData.callDate).format("DD MMM YYYY")}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <Tag style={{
                      borderRadius:20, fontWeight:700, fontSize:11,
                      color:cfg.color, background:cfg.bg, border:`1px solid ${cfg.border}`,
                      margin:0,
                    }}>{st}</Tag>
                    {isLogged && (
                      <span style={{fontSize:11, color:C.text3}}>
                        {isOpen ? "▲" : "▼"}
                      </span>
                    )}
                    {emp.isResigned ? (
                      <span style={{fontSize:11,color:C.red,fontWeight:600}}>🚪 Resigned</span>
                    ) : (
                      <LogCallButton employee={emp} callKey={key} onSaved={refresh} size="small" />
                    )}
                  </div>
                </div>

                {/* ── Expanded: all history entries for this stage ── */}
                {isLogged && isOpen && (() => {
                  const stageHistory = callHistory
                    .filter(h => h.callKey === key)
                    .sort((a,b) => new Date(b.loggedAt) - new Date(a.loggedAt));  // newest first

                  return (
                    <div style={{padding:"14px 16px", borderTop:`1px solid ${isComp?"#dcfce7":"#dce6f0"}`}}>

                      {stageHistory.length === 0 ? (
                        // Fallback: show current call data if no history rows
                        <HistoryEntry entry={{
                          status:   callData.status || st,
                          callDate: callData.callDate,
                          remarks:  callData.remarks,
                          snapshot: callData,
                          loggedAt: callData.callDate,
                        }} callKey={key} isLatest={true} />
                      ) : (
                        <HistoryEntry key={stageHistory[0].id} entry={stageHistory[0]} callKey={key} isLatest={true} />
                      )}
                    </div>
                  );
                })()}

              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TAB: Profile
// ══════════════════════════════════════════════════════════════════════════════
function ProfileTab({ emp, refresh }) {
  const [editing,  setEditing]  = React.useState(null);  // "contact" | "manager" | "aagman"
  const [saving,   setSaving]   = React.useState(false);
  const [form]                  = Form.useForm();

  // Lock rule: aagman locked if status=Attended AND date is set
  const aagmanLocked = emp.aagmanStatus === "Attended" && !!emp.aagmanDate;

  function startEdit(section) {
    if (section === "personal") {
      form.setFieldsValue({ empCode: emp.empCode || "", division: emp.division || "" });
    } else if (section === "contact") {
      form.setFieldsValue({
        mobile:        emp.mobile        || "",
        empEmail:      emp.empEmail      || "",
        dateOfJoining: emp.dateOfJoining ? dayjs(emp.dateOfJoining) : null,
      });
    } else if (section === "manager") {
      form.setFieldsValue({
        rmCode:        emp.rmCode        || "",
        rmName:        emp.rmName        || "",
        managerEmail:  emp.managerEmail  || "",
        managerMobile: emp.managerMobile || "",
      });
    } else if (section === "aagman") {
      form.setFieldsValue({
        aagmanStatus: emp.aagmanStatus || null,
        aagmanDate:   emp.aagmanDate ? dayjs(emp.aagmanDate) : null,
      });
    }
    setEditing(section);
  }

  async function handleSave() {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const payload = { ...values };
      if (payload.aagmanDate)    payload.aagmanDate    = payload.aagmanDate.format("YYYY-MM-DD");
      if (payload.dateOfJoining) payload.dateOfJoining = payload.dateOfJoining.format("YYYY-MM-DD");
      await employeeService.update(emp.id, payload);
      notification.success({ message: "Profile updated", placement: "topRight" });
      setEditing(null);
      refresh?.();
    } catch (err) {
      if (err?.errorFields) return;
      notification.error({ message: err.message || "Failed to save" });
    } finally {
      setSaving(false);
    }
  }

  // Read-only field row
  const Field = ({ label, value }) => (
    <div className="ed-profile-field">
      <span className="ed-profile-label">{label}</span>
      <span className="ed-profile-value">{value || "—"}</span>
    </div>
  );

  // Edit button
  const EditBtn = ({ section, locked, lockMsg }) => (
    locked
      ? <Tooltip title={lockMsg || "Cannot edit"}>
          <span style={{fontSize:11,color:C.text3,fontStyle:"italic"}}>🔒 Locked</span>
        </Tooltip>
      : editing !== section
        ? <Button size="small" icon={<EditOutlined />} onClick={() => startEdit(section)}
            style={{fontSize:11}}>Edit</Button>
        : <div style={{display:"flex",gap:6}}>
            <Button size="small" onClick={() => setEditing(null)}>Cancel</Button>
            <Button size="small" type="primary" loading={saving} onClick={handleSave}
              style={{background:C.navy}}>Save</Button>
          </div>
  );

  return (
    <div className="ed-tab-content">
      <Form form={form} layout="vertical">
      <div className="ed-profile-grid">

        {/* ── Personal Information (read-only) ── */}
        <div className="ed-profile-card">
          <div className="ed-profile-card-title" style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span><span className="ed-profile-card-icon"><UserOutlined /></span>Personal Information</span>
            <EditBtn section="personal" />
          </div>

          {editing === "personal" ? (
            <div style={{marginBottom:10}}>
              <Form.Item name="empCode" label="Employee Code"
                rules={[{required:true,message:"Employee code is required"}]}
                style={{marginBottom:12}}>
                <Input placeholder="e.g. L04480" style={{textTransform:"uppercase"}}
                  onChange={e => form.setFieldValue("empCode", e.target.value.toUpperCase())} />
              </Form.Item>
              <Form.Item name="division" label="Division" style={{marginBottom:0}}>
                <Input placeholder="e.g. Aspire" />
              </Form.Item>
            </div>
          ) : (
            <Field label="Employee Code" value={emp.empCode} />
          )}
          <Field label="Full Name"      value={emp.empName} />
          <Field label="Designation"    value={emp.designation} />
          {editing !== "personal" && <Field label="Division" value={emp.division} />}
          <Field label="HQ / Location"  value={emp.hq} />
          <Field label="Joined Month"   value={emp.joinedMonth} />

          {/* Aagman — editable unless locked */}
          <div className="ed-profile-field" style={{alignItems:"flex-start",flexDirection:"column",gap:6}}>
            <div style={{display:"flex",justifyContent:"space-between",width:"100%",alignItems:"center"}}>
              <span className="ed-profile-label">Aagman Status / Date</span>
              <EditBtn section="aagman"
                locked={aagmanLocked}
                lockMsg="Aagman status is Attended with a date set — cannot be changed" />
            </div>
            {editing === "aagman" ? (
              <div style={{display:"flex",gap:10,flexWrap:"wrap",width:"100%"}}>
                <Form.Item name="aagmanStatus" style={{marginBottom:0,flex:1}}>
                  <Select placeholder="Status" options={[
                    {value:"Pending",  label:"Pending"},
                    {value:"Attended", label:"Attended"},
                    {value:"NA",       label:"NA"},
                  ]} />
                </Form.Item>
                <Form.Item name="aagmanDate" style={{marginBottom:0,flex:1}}>
                  <DatePicker format="DD-MMM-YYYY" placeholder="Attend Date" style={{width:"100%"}} allowClear />
                </Form.Item>
              </div>
            ) : (
              <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                {emp.aagmanStatus
                  ? <Tag style={{borderRadius:20,fontWeight:600,margin:0}}>{emp.aagmanStatus}</Tag>
                  : <span style={{color:C.text3}}>—</span>}
                {emp.aagmanDate && (
                  <span style={{fontSize:12,color:C.text2}}>
                    {dayjs(emp.aagmanDate).format("DD MMM YYYY")}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ── Contact Details (editable) ── */}
        <div className="ed-profile-card">
          <div className="ed-profile-card-title" style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span><span className="ed-profile-card-icon"><PhoneOutlined /></span>Contact Details</span>
            <EditBtn section="contact" />
          </div>

          {editing === "contact" ? (
            <>
              <Form.Item name="mobile" label="Mobile"
                rules={[{pattern:/^\d{10}$/,message:"10-digit number"}]}
                style={{marginBottom:12}}>
                <Input placeholder="10-digit mobile" maxLength={10} />
              </Form.Item>
              <Form.Item name="empEmail" label="Email"
                rules={[{type:"email",message:"Invalid email"}]}
                style={{marginBottom:12}}>
                <Input placeholder="email@erislifesciences.com" />
              </Form.Item>
              <Form.Item name="dateOfJoining" label="Date of Joining"
                style={{marginBottom:0}}>
                <DatePicker format="DD MMM YYYY" style={{width:"100%"}} />
              </Form.Item>
            </>
          ) : (
            <>
              <Field label="Mobile"           value={emp.mobile} />
              <Field label="Email"            value={emp.empEmail} />
            </>
          )}
          {editing !== "contact" && (
            <Field label="Date of Joining" value={emp.dateOfJoining ? dayjs(emp.dateOfJoining).format("DD MMM YYYY") : "—"} />
          )}
          <Field label="Initial Call Date"  value={emp.callDate ? dayjs(emp.callDate).format("DD MMM YYYY") : "—"} />
        </div>

        {/* ── Reporting Manager (editable) ── */}
        <div className="ed-profile-card">
          <div className="ed-profile-card-title" style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
            <span><span className="ed-profile-card-icon"><TeamOutlined /></span>Reporting Manager</span>
            <EditBtn section="manager" />
          </div>

          {editing === "manager" ? (
            <>
              <Form.Item name="rmCode" label="RM Code" style={{marginBottom:10}}>
                <Input placeholder="e.g. M01234" />
              </Form.Item>
              <Form.Item name="rmName" label="RM Name" style={{marginBottom:10}}>
                <Input placeholder="Manager full name" />
              </Form.Item>
              <Form.Item name="managerEmail" label="Manager Email"
                rules={[{type:"email",message:"Invalid email"}]} style={{marginBottom:10}}>
                <Input placeholder="manager@erislifesciences.com" />
              </Form.Item>
              <Form.Item name="managerMobile" label="Manager Mobile"
                rules={[{pattern:/^\d{10}$/,message:"10-digit number"}]} style={{marginBottom:0}}>
                <Input placeholder="10-digit mobile" maxLength={10} />
              </Form.Item>
            </>
          ) : (
            <>
              <Field label="RM Code"         value={emp.rmCode} />
              <Field label="RM Name"         value={emp.rmName} />
              <Field label="Manager Email"   value={emp.managerEmail} />
              <Field label="Manager Mobile"  value={emp.managerMobile} />
            </>
          )}
        </div>

      </div>
      </Form>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TAB: Manager Follow-up
// ══════════════════════════════════════════════════════════════════════════════
function ManagerTab({ emp, refresh }) {
  const mf = emp.managerFollowup || {};
  const [editing, setEditing] = React.useState(false);
  const [saving,  setSaving]  = React.useState(false);
  const [form]               = Form.useForm();

  const ATTEN_OPTS = [
    {value:"Present",label:"Present"},{value:"Absent",label:"Absent"},
  ];
  const IPAD_OPTS = [
    {value:"Active",label:"Active"},{value:"Pending",label:"Pending"},{value:"Not Issued",label:"Not Issued"},
  ];

  function startEdit() {
    form.setFieldsValue({
      prathamDate:        mf.prathamDate ? dayjs(mf.prathamDate) : null,
      accompanied:        mf.accompanied        ?? false,
      ipadProvided:       mf.ipadProvided        ?? false,
      ipadStatus:         mf.ipadStatus          || null,
      prathamCandidAtten: mf.prathamCandidAtten  || null,
      prathamMgrAtten:    mf.prathamMgrAtten     || null,
      mgrRemarks:         mf.mgrRemarks          || "",
    });
    setEditing(true);
  }

  async function handleSave() {
    setSaving(true);
    try {
      const v = form.getFieldsValue();
      await employeeService.update(emp.id, {
        managerFollowup: {
          prathamDate:        v.prathamDate ? v.prathamDate.format("YYYY-MM-DD") : null,
          accompanied:        v.accompanied,
          ipadProvided:       v.ipadProvided,
          ipadStatus:         v.ipadStatus         || "",
          prathamCandidAtten: v.prathamCandidAtten  || "",
          prathamMgrAtten:    v.prathamMgrAtten     || "",
          mgrRemarks:         v.mgrRemarks          || "",
        },
      });
      notification.success({ message: "Manager Follow-up saved", placement: "topRight" });
      setEditing(false);
      refresh?.();
    } catch (err) {
      notification.error({ message: err.message || "Failed to save" });
    } finally {
      setSaving(false);
    }
  }

  const yesNo = (v) => {
    if (!v && v !== true && v !== false) return <span style={{color:C.text3}}>—</span>;
    const display = v === true ? "Yes" : v === false ? "No" : v;
    const col = ["Yes","Present","Provided","Issued","true"].includes(String(display))
      ? C.green : ["No","Absent","false"].includes(String(display)) ? C.red : C.amber;
    return <Tag style={{borderRadius:20,fontSize:11,fontWeight:700,
      background:col+"18",border:`1px solid ${col}44`,color:col}}>{display}</Tag>;
  };

  const Field = ({label, value}) => (
    <div className="ed-profile-field">
      <span className="ed-profile-label">{label}</span>
      <span className="ed-profile-value">{yesNo(value)}</span>
    </div>
  );

  return (
    <div className="ed-tab-content">
      <div className="ed-profile-card" style={{maxWidth:640}}>

        {/* Header with Edit/Save/Cancel */}
        <div className="ed-profile-card-title"
          style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
          <span>
            <span className="ed-profile-card-icon"><TeamOutlined /></span>
            Manager Follow-up (Day -1)
          </span>
          {!editing ? (
            <Button size="small" icon={<EditOutlined />} onClick={startEdit}>Edit</Button>
          ) : (
            <div style={{display:"flex",gap:8}}>
              <Button size="small" onClick={()=>setEditing(false)}>Cancel</Button>
              <Button size="small" type="primary" loading={saving}
                onClick={handleSave} style={{background:C.navy}}>Save</Button>
            </div>
          )}
        </div>

        {/* View mode */}
        {!editing && (
          <>
            <Field label="Pratham Date"
              value={mf.prathamDate ? dayjs(mf.prathamDate).format("DD MMM YYYY") : null} />
            <Field label="Accompanied BE during Pratham" value={mf.accompanied} />
            <Field label="iPad / Laptop Provided"        value={mf.ipadProvided} />
            <Field label="iPad Status"                   value={mf.ipadStatus} />
            <Field label="Pratham Candidate Attendance"  value={mf.prathamCandidAtten} />
            <Field label="Pratham Manager Attendance"    value={mf.prathamMgrAtten} />
            <Field label="Manager Remarks"               value={mf.mgrRemarks} />
          </>
        )}

        {/* Edit mode */}
        {editing && (
          <Form form={form} layout="vertical" style={{marginTop:12}}>
            <Form.Item name="prathamDate" label="Pratham Date">
              <DatePicker style={{width:"100%"}} format="DD-MMM-YYYY"
                placeholder="Select Pratham date" allowClear />
            </Form.Item>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 20px"}}>
              <Form.Item name="accompanied" label="Accompanied BE" valuePropName="checked">
                <Select options={[
                  {value:true, label:"Yes"},{value:false,label:"No"},
                ]} placeholder="Select" />
              </Form.Item>
              <Form.Item name="ipadProvided" label="iPad / Laptop Provided" valuePropName="checked">
                <Select options={[
                  {value:true, label:"Yes"},{value:false,label:"No"},
                ]} placeholder="Select" />
              </Form.Item>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 20px"}}>
              <Form.Item name="ipadStatus" label="iPad Status">
                <Select placeholder="Select" allowClear options={IPAD_OPTS} />
              </Form.Item>
              <Form.Item name="prathamCandidAtten" label="Pratham Candidate Attendance">
                <Select placeholder="Select" allowClear options={ATTEN_OPTS} />
              </Form.Item>
            </div>
            <Form.Item name="prathamMgrAtten" label="Pratham Manager Attendance">
              <Select placeholder="Select" allowClear options={ATTEN_OPTS}
                style={{maxWidth:280}} />
            </Form.Item>
            <Form.Item name="mgrRemarks" label="Manager Remarks">
              <Input.TextArea rows={3}
                placeholder="Any remarks from the manager..." />
            </Form.Item>
          </Form>
        )}

      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// TAB: Day-10 Responses
// ══════════════════════════════════════════════════════════════════════════════
function Day10Tab({ emp, callKey = "day10" }) {
  const day10Questions = useDay10Questions();
  const calls = emp?.calls || [];
  // Use callKey prop so Campus day30 shows its Q&A responses
  const d10 = Array.isArray(calls)
    ? (calls.find((c) => c.callKey === callKey) || {})
    : (calls?.[callKey] || {});
  const answered = day10Questions.filter((q) => d10[q.key]).length;

  if (!d10.callDate) {
    return (
      <div className="ed-tab-content">
        <div className="ed-empty-state">
          <ClockCircleOutlined style={{ fontSize:36, color:C.text3 }} />
          <div style={{ color:C.text3, marginTop:10, fontSize:13 }}>
            Day-10 call has not been logged yet.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ed-tab-content">
      {/* Summary row */}
      <div className="ed-d10-summary-row">
        <div className="ed-d10-stat ed-d10-stat--blue">
          <div className="ed-d10-stat-val">{dayjs(d10.callDate).format("DD MMM YYYY")}</div>
          <div className="ed-d10-stat-lbl">Call Date</div>
        </div>
        <div className="ed-d10-stat ed-d10-stat--green">
          <div className="ed-d10-stat-val">{day10Questions.filter((q) => (q.answerType||"yesno")==="yesno" && d10[q.key] === "Yes").length}</div>
          <div className="ed-d10-stat-lbl">Yes</div>
        </div>
        <div className="ed-d10-stat ed-d10-stat--red">
          <div className="ed-d10-stat-val">{day10Questions.filter((q) => (q.answerType||"yesno")==="yesno" && d10[q.key] === "No").length}</div>
          <div className="ed-d10-stat-lbl">No</div>
        </div>
        <div className="ed-d10-stat ed-d10-stat--grey">
          <div className="ed-d10-stat-val">{day10Questions.filter((q) => (q.answerType||"yesno")==="yesno" && d10[q.key] === "NA").length}</div>
          <div className="ed-d10-stat-lbl">NA</div>
        </div>
        <div className="ed-d10-stat ed-d10-stat--blue">
          <div className="ed-d10-stat-val">{answered}/{day10Questions.length}</div>
          <div className="ed-d10-stat-lbl">Answered</div>
        </div>
      </div>

      {/* Responses list */}
      <div className="ed-d10-responses">
        {day10Questions.map((q, idx) => {
          const ans        = d10[q.key] || "";
          const answerType = q.answerType || "yesno";
          const ratingVal  = parseInt(ans, 10);
          // Color for yesno; rating uses traffic-light; choice uses blue
          const col = answerType === "rating5"
            ? (ratingVal >= 4 ? "#16a34a" : ratingVal === 3 ? "#b45309" : ans ? "#dc2626" : "#b0c4d8")
            : answerType === "choice"
              ? (ans ? "#1E6FBF" : "#b0c4d8")
              : (DAY10_ANSWER_COLOR[ans] || "#b0c4d8");
          return (
            <div key={q.key} className="ed-d10-response-row">
              <div className="ed-d10-q-num">{idx + 1}</div>
              <div className="ed-d10-q-text">{q.label}</div>
              <div className="ed-d10-ans"
                style={{
                  color: col,
                  background: answerType==="rating5"
                    ? (ratingVal>=4?"#f0fdf4":ratingVal===3?"#fffbeb":ans?"#fef2f2":"#f9fafb")
                    : answerType==="choice"
                      ? (ans?"#eff6ff":"#f9fafb")
                      : (ans==="Yes"?"#f0fdf4":ans==="No"?"#fef2f2":ans==="NA"?"#f5f8fc":"#f9fafb"),
                  borderColor: col+"44",
                  minWidth: answerType==="choice"?80:36,
                  fontSize: answerType==="choice"?10:11,
                }}>
                {answerType==="rating5" ? (ans ? `${ans}/5` : "—") : (ans || "—")}
              </div>
            </div>
          );
        })}
      </div>

      {d10.remarks && (
        <div className="ed-d10-remarks-box">
          <div style={{ fontSize:11, fontWeight:700, color:C.text3, textTransform:"uppercase", letterSpacing:"0.6px", marginBottom:6 }}>Remarks</div>
          <div style={{ fontSize:13, color:C.text2 }}>{d10.remarks}</div>
        </div>
      )}
    </div>
  );
}